import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { adminApi } from '../../services/api';

const C = {
  accent: '#c96428', accentL: '#fff4ee', accentB: '#f0d0b0',
  text1: '#1a0f00', text3: '#6b4c35', text4: '#a07858',
  border: '#f0e4d8', card: '#ffffff', surface: '#fffaf7',
};

const BASE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

const AdminTables = () => {
  const { slug } = useParams();

  const [tables,        setTables]        = useState([]);
  const [newTable,      setNewTable]      = useState('');
  const [qrImages,      setQrImages]      = useState({});
  const [selectedTable, setSelectedTable] = useState(null);
  const [generating,    setGenerating]    = useState(false);
  const [loadingTables, setLoadingTables] = useState(true);

  // DB se tables load karo
  useEffect(() => {
    adminApi.getTables()
      .then(r => setTables(r.data.data || []))
      .catch(() => toast.error('Failed to load tables'))
      .finally(() => setLoadingTables(false));
  }, []);

  const addTable = async () => {
    const name = newTable.trim();
    if (!name) return toast.error('Table name/number required');
    try {
      const res = await adminApi.addTable({ table_number: name });
      setTables(prev => [...prev, res.data.data]);
      setNewTable('');
      toast.success(`Table "${name}" added!`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add table');
    }
  };

  const removeTable = async (table) => {
    try {
      await adminApi.deleteTable(table.id);
      setTables(prev => prev.filter(t => t.id !== table.id));
      setQrImages(prev => { const n = { ...prev }; delete n[table.id]; return n; });
      if (selectedTable?.id === table.id) setSelectedTable(null);
      toast.success('Table removed');
    } catch {
      toast.error('Failed to remove table');
    }
  };

  // ✅ Slug included in QR URL
  const getTableUrl = (tableName) =>
    `${BASE_URL}/${slug}/order?table=${encodeURIComponent(tableName)}`;

  const generateQR = async (table) => {
    setGenerating(true);
    try {
      const url = getTableUrl(table.table_number);
      const dataUrl = await QRCode.toDataURL(url, {
        width: 400, margin: 2,
        color: { dark: '#1a0f00', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
      setQrImages(prev => ({ ...prev, [table.id]: { dataUrl, url } }));
      setSelectedTable(table);
    } catch {
      toast.error('QR generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const generateAllQR = async () => {
    setGenerating(true);
    const newQrs = {};
    for (const table of tables) {
      try {
        const url = getTableUrl(table.table_number);
        const dataUrl = await QRCode.toDataURL(url, {
          width: 400, margin: 2,
          color: { dark: '#1a0f00', light: '#ffffff' },
          errorCorrectionLevel: 'H',
        });
        newQrs[table.id] = { dataUrl, url };
      } catch {}
    }
    setQrImages(prev => ({ ...prev, ...newQrs }));
    setGenerating(false);
    toast.success('All QR codes generated!');
  };

  const printQR = (table) => {
    const qr = qrImages[table.id];
    if (!qr) return toast.error('Generate QR first');
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Table ${table.table_number} QR</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #fdf8f4; }
          .card { text-align: center; padding: 40px 48px; background: white; border-radius: 20px; box-shadow: 0 8px 32px rgba(0,0,0,0.1); border: 2px solid #f0d0b0; max-width: 340px; }
          .cafe-name { font-size: 22px; font-weight: 800; color: #c96428; margin-bottom: 4px; }
          .scan-text { font-size: 13px; color: #8b6347; margin-bottom: 20px; }
          .qr-img { width: 220px; height: 220px; border-radius: 12px; border: 3px solid #f0d0b0; padding: 6px; }
          .table-name { font-size: 28px; font-weight: 800; color: #1a0f00; margin-top: 20px; }
          .table-label { font-size: 13px; color: #8b6347; margin-top: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
          @media print { body { background: white; } .card { box-shadow: none; } }
        </style>
      </head>
      <body>
        <div class="card">
          <p class="cafe-name">☕ ${slug}</p>
          <p class="scan-text">Scan to Order</p>
          <img src="${qr.dataUrl}" class="qr-img" alt="QR Code" />
          <p class="table-name">Table ${table.table_number}</p>
          <p class="table-label">Scan &amp; Order Instantly</p>
        </div>
        <script>window.onload = () => { window.print(); }<\/script>
      </body>
      </html>
    `);
    win.document.close();
  };

  const printAll = () => {
    const hasSome = tables.some(t => qrImages[t.id]);
    if (!hasSome) return toast.error('Generate QR codes first');
    const win = window.open('', '_blank');
    const cards = tables
      .filter(t => qrImages[t.id])
      .map(t => `
        <div class="card">
          <p class="cafe-name">☕ ${slug}</p>
          <p class="scan-text">Scan to Order</p>
          <img src="${qrImages[t.id].dataUrl}" class="qr-img" alt="QR Code" />
          <p class="table-name">Table ${t.table_number}</p>
          <p class="table-label">Scan &amp; Order Instantly</p>
        </div>
      `).join('');
    win.document.write(`
      <!DOCTYPE html><html><head><title>All Table QRs</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #fdf8f4; padding: 20px; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .card { text-align: center; padding: 28px 20px; background: white; border-radius: 16px; border: 2px solid #f0d0b0; break-inside: avoid; }
        .cafe-name { font-size: 16px; font-weight: 800; color: #c96428; margin-bottom: 2px; }
        .scan-text { font-size: 11px; color: #8b6347; margin-bottom: 14px; }
        .qr-img { width: 160px; height: 160px; border-radius: 10px; border: 2px solid #f0d0b0; padding: 4px; }
        .table-name { font-size: 22px; font-weight: 800; color: #1a0f00; margin-top: 12px; }
        .table-label { font-size: 11px; color: #8b6347; margin-top: 2px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
        @media print { body { background: white; } @page { size: A4; margin: 15mm; } }
      </style></head>
      <body><div class="grid">${cards}</div>
      <script>window.onload = () => window.print();<\/script>
      </body></html>
    `);
    win.document.close();
  };

  const downloadQR = (table) => {
    const qr = qrImages[table.id];
    if (!qr) return toast.error('Generate QR first');
    const a = document.createElement('a');
    a.href = qr.dataUrl;
    a.download = `table-${table.table_number}-qr.png`;
    a.click();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text1, margin: 0 }}>Table QR Codes</h2>
          <p style={{ color: C.text4, fontSize: 13, marginTop: 4 }}>
            {tables.length} table{tables.length !== 1 ? 's' : ''} · Customers scan QR to order directly
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {tables.length > 0 && (
            <>
              <button onClick={generateAllQR} disabled={generating}
                style={{
                  padding: '10px 18px', borderRadius: 11, border: `1.5px solid ${C.accentB}`,
                  background: C.accentL, color: C.accent, fontWeight: 700, fontSize: 13,
                  cursor: generating ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                }}>
                {generating ? '⏳ Generating...' : '⚡ Generate All QRs'}
              </button>
              <button onClick={printAll}
                style={{
                  padding: '10px 18px', borderRadius: 11, border: 'none',
                  background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: 'white',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 4px 14px rgba(201,100,40,0.3)',
                }}>
                🖨️ Print All
              </button>
            </>
          )}
        </div>
      </div>

      {/* Add Table */}
      <div style={{
        background: C.card, borderRadius: 18, padding: '22px 24px',
        border: `1px solid ${C.border}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}>
        <p style={{ fontWeight: 700, color: C.text1, fontSize: 15, marginBottom: 14 }}>➕ Add New Table</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            placeholder="Table name or number (e.g. 1, A1, Window, Terrace)"
            value={newTable}
            onChange={e => setNewTable(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTable()}
            style={{
              flex: 1, padding: '13px 16px', borderRadius: 12, fontSize: 14,
              border: `1.5px solid ${C.border}`, outline: 'none', fontFamily: 'inherit',
              background: C.surface, color: C.text1,
            }}
          />
          <button onClick={addTable}
            style={{
              padding: '13px 24px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: 'white',
              fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 4px 14px rgba(201,100,40,0.3)', whiteSpace: 'nowrap',
            }}>
            Add Table
          </button>
        </div>
        <p style={{ color: C.text4, fontSize: 12, marginTop: 10 }}>
          💡 Tip: Use numbers (1, 2, 3), letters (A, B), or names (Window, Terrace, VIP)
        </p>
      </div>

      {/* Tables Grid */}
      {loadingTables ? (
        <div style={{ textAlign: 'center', padding: '40px', color: C.text4 }}>Loading tables...</div>
      ) : tables.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 24px', background: C.card,
          borderRadius: 18, border: `1px solid ${C.border}`,
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🪑</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: C.text1, marginBottom: 8 }}>No Tables Yet</h3>
          <p style={{ color: C.text4, fontSize: 14 }}>Add tables above to generate QR codes for them</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {tables.map(table => {
            const qr = qrImages[table.id];
            return (
              <motion.div key={table.id}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                style={{
                  background: C.card, borderRadius: 18, padding: '20px',
                  border: `1.5px solid ${selectedTable?.id === table.id ? C.accent : C.border}`,
                  boxShadow: selectedTable?.id === table.id
                    ? '0 4px 20px rgba(201,100,40,0.15)' : '0 2px 12px rgba(0,0,0,0.04)',
                  display: 'flex', flexDirection: 'column', gap: 14, cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => qr && setSelectedTable(table)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: 11, color: C.text4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Table</p>
                    <p style={{ fontSize: 26, fontWeight: 800, color: C.text1, lineHeight: 1.2 }}>{table.table_number}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removeTable(table); }}
                    style={{
                      width: 32, height: 32, borderRadius: '50%', border: '1px solid #fecaca',
                      background: '#fff5f5', color: '#ef4444', fontSize: 14,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>×</button>
                </div>

                {qr ? (
                  <div style={{ textAlign: 'center' }}>
                    <img src={qr.dataUrl} alt={`QR Table ${table.table_number}`}
                      style={{ width: '100%', maxWidth: 180, borderRadius: 12, border: `2px solid ${C.border}`, padding: 4 }} />

                  </div>
                ) : (
                  <div style={{
                    height: 140, borderRadius: 12, background: C.surface,
                    border: `2px dashed ${C.accentB}`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8,
                  }}>
                    <span style={{ fontSize: 32 }}>📱</span>
                    <p style={{ fontSize: 12, color: C.text4 }}>QR not generated</p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  {!qr ? (
                    <button onClick={(e) => { e.stopPropagation(); generateQR(table); }} disabled={generating}
                      style={{
                        flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                        background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: 'white',
                        fontWeight: 700, fontSize: 12, cursor: generating ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                      }}>
                      {generating ? '⏳' : '⚡ Generate QR'}
                    </button>
                  ) : (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); printQR(table); }}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                          background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: 'white',
                          fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        🖨️ Print
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); downloadQR(table); }}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 10,
                          border: `1.5px solid ${C.accentB}`, background: C.accentL,
                          color: C.accent, fontWeight: 700, fontSize: 12,
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        ⬇️ Save
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* QR Preview Modal */}
      <AnimatePresence>
        {selectedTable && qrImages[selectedTable.id] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
            onClick={() => setSelectedTable(null)}
          >
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'white', borderRadius: 24, padding: '40px 48px',
                textAlign: 'center', maxWidth: 380, width: '100%',
                boxShadow: '0 24px 64px rgba(0,0,0,0.3)', border: `2px solid ${C.border}`,
              }}
            >
              <p style={{ fontSize: 13, color: C.text4, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                {slug}
              </p>
              <p style={{ fontSize: 14, color: C.text3, marginBottom: 20 }}>Scan to Order</p>

              <img src={qrImages[selectedTable.id].dataUrl} alt="QR Code"
                style={{ width: 220, height: 220, borderRadius: 16, border: `3px solid ${C.border}`, padding: 8, marginBottom: 20 }} />

              <p style={{ fontSize: 32, fontWeight: 800, color: C.text1, marginBottom: 4 }}>
                Table {selectedTable.table_number}
              </p>


              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => printQR(selectedTable)}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: 'white',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                    boxShadow: '0 4px 14px rgba(201,100,40,0.3)',
                  }}>
                  🖨️ Print
                </button>
                <button onClick={() => downloadQR(selectedTable)}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 12,
                    border: `1.5px solid ${C.accentB}`, background: C.accentL,
                    color: C.accent, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  ⬇️ Download
                </button>
              </div>

              <button onClick={() => setSelectedTable(null)}
                style={{
                  width: '100%', marginTop: 10, padding: '13px', borderRadius: 12,
                  border: `1px solid ${C.border}`, background: 'white',
                  color: C.text3, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTables;
